/* eslint-disable class-methods-use-this */
/* eslint-disable camelcase */
/* eslint-disable @typescript-eslint/no-explicit-any */
import pkceChallenge from 'pkce-challenge'
import { Subject } from 'rxjs'
import { filter, take, map } from 'rxjs/operators'
import { v4 } from 'uuid'

const AUTH_SW_SCRIPT = '/auth.sw.js'

export enum AUTH_SERVICE_EVENTS {
  INIT = 'INIT',
  IS_AUTH = 'IS_AUTH',
  SET_AUTH_CHALLENGE = 'SET_AUTH_CHALLENGE',
  SET_AUTH_RESPONSE = 'SET_AUTH_RESPONSE',
  RELOAD = 'RELOAD',
  LOGOUT = 'LOGOUT',
}

export interface AuthServiceEvent {
  type: AUTH_SERVICE_EVENTS
  data: any
}

export interface AuthenticateOptions {
  redirectUrl?: string
  state?: string
}

export default class AuthService {
  subject = new Subject<AuthServiceEvent>()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  controller: ServiceWorker = {} as ServiceWorker
  pkce: {
    code_challenge: string
    code_verifier: string
  } = {
    code_challenge: '',
    code_verifier: '',
  }

  constructor(private apiGateway: string, private clientId: string, private brand: string) {
    navigator.serviceWorker.onmessage = (event): unknown => {
      return this.subject.next(event.data)
    }
  }

  isAuthenticated() {
    this.controller.postMessage({
      type: AUTH_SERVICE_EVENTS.IS_AUTH,
    })
    return this.subject.pipe(
      filter((evt) => evt.type === AUTH_SERVICE_EVENTS.IS_AUTH),
      take(1),
      map((evt) => {
        return !!evt.data
      }),
    )
  }

  getOauthURL(options: {
    state?: string
    oauthCallbackURL?: string
    pkce: ReturnType<typeof pkceChallenge>
  }) {
    const params = new URLSearchParams()
    params.set('client_id', this.clientId)
    params.set('response_type', 'code')
    params.set('state', v4())
    params.set('access_type', 'online')
    params.set('code_challenge', options.pkce.code_challenge)
    params.set('code_challenge_method', 'S256')
    params.set(
      'groups_hint',
      'pos salesonlinedistribution salesinbound salesoutbound careretention careloyalty careunsubscribe salescancellation salesundercustomerbase salesbroadcastcustomerbase careadministrator customercare',
    )
    params.set(
      'redirect_uri',
      encodeURI(`${globalThis.location.origin}/${options.oauthCallbackURL || 'oauth.html'}`),
    )

    return `${this.apiGateway}/oauth/authorize?${params.toString()}`
  }

  authenticate(options: AuthenticateOptions = {}) {
    this.pkce = pkceChallenge()

    window.open(
      this.getOauthURL({ ...options, pkce: this.pkce }),
      'login',
      'toolbar=no, menubar=no, width=600, height=700, top=100, left=100',
    )

    this.setAuthChallenge(options.state)
  }

  authenticationDataSubs() {
    return this.subject.pipe(
      filter((evt: AuthServiceEvent) => evt.type === AUTH_SERVICE_EVENTS.SET_AUTH_RESPONSE),
      map((evt: AuthServiceEvent) => {
        return evt.data
      }),
    )
  }

  setAuthChallenge(from?: string) {
    this.controller.postMessage({
      type: AUTH_SERVICE_EVENTS.SET_AUTH_CHALLENGE,
      data: { ...this.pkce, from },
    })
  }

  logout() {
    this.controller.postMessage({
      type: AUTH_SERVICE_EVENTS.LOGOUT,
    })
  }

  reloadSubs() {
    return this.subject.pipe(
      filter((evt: AuthServiceEvent) => evt.type === AUTH_SERVICE_EVENTS.RELOAD),
      map((evt: AuthServiceEvent) => {
        return evt.data
      }),
    )
  }

  authSubs() {
    return this.subject.pipe(
      filter((evt: AuthServiceEvent) => evt.type === AUTH_SERVICE_EVENTS.IS_AUTH),
      map((evt: AuthServiceEvent) => {
        return evt.data
      }),
    )
  }

  setAuthCode(state: string, code: string | null) {
    this.controller.postMessage({ type: 'SET_AUTH_CODE', data: { state, code } })
  }

  init() {
    if (!('serviceWorker' in globalThis.navigator)) {
      return Promise.reject()
    }

    return globalThis.navigator.serviceWorker
      .register(AUTH_SW_SCRIPT)
      .then(() => globalThis.navigator.serviceWorker.ready)
      .then((reg) => {
        if (reg.active) {
          if (reg.active.state === 'redundant') {
            window.location.reload()
          }
          this.controller = reg.active
          this.controller.postMessage({
            type: AUTH_SERVICE_EVENTS.INIT,
            data: {
              clientId: this.clientId,
              apiGateway: this.apiGateway,
              brand: this.brand,
              host: process.env.PUBLIC_URL,
            },
          })
        }
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.warn('Error during service worker registration:', error)
        throw error
      })
  }

  unregister() {
    if ('serviceWorker' in navigator) {
      globalThis.navigator.serviceWorker.ready.then((registration) => {
        registration.unregister()
      })
    }
  }
}
